import { LEARNING_PATH_TYPES, LEARNING_STATES } from '@constants/learningStates.ts';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    LearningPathType,
    LearningResponse,
    LearningResultsDescription,
    UserScoreResponse,
} from '@routes/sidebar/learning/api/learning.api.ts';
import {
    deleteApiMarkPointAsSeen,
    getApiLearning,
    getApiLearningBookmarks,
    getApiPoints,
    putApiToggleLearningBookmark,
} from '@routes/sidebar/learning/api/learning.api.ts';
import type { CommonSearchRequest, PaginatedResponse } from '@types';

interface LearningSlice {
    error: Record<LearningPathType, Record<string, any> | null>;
    pointsError: Record<string, any> | string | null;

    learningCards: Record<LearningPathType, PaginatedResponse<LearningResponse, LearningResultsDescription>>;
    learningCardsRequestData: Record<LearningPathType, CommonSearchRequest>;
    bookmarks: number[];
    userScoreResponse: UserScoreResponse;

    isGetMandatoryLearningCardsPending: boolean;
    isGetRecommendedLearningCardsPending: boolean;
    isGetLibraryLearningCardsPending: boolean;
    isGetCareerRoadmapLearningCardsPending: boolean;
    isGetBookmarksPending: boolean;
    isToggleBookmarkPending: boolean;
    isGetPointsPending: boolean;
    isMarkPointAsSeenPending: boolean;
}

const INITIAL_REQUEST: CommonSearchRequest = {
    textSearch: '',
    filters: null,
    pageNumber: 1,
    pageSize: 9,
};

const INITIAL_DATA: PaginatedResponse<LearningResponse, LearningResultsDescription> = {
    data: [],
    pagination: {
        maxPageSize: 9,
        pageNumber: 1,
        pageSize: 9,
        totalCount: 0,
        totalPages: 0,
    },
    resultsDescription: {
        allPathwaysCompleted: false,
        overdue: 0,
        ranks: [],
        lastPathwayIdUsed: null,
        lastElementIdCompleted: null,
    },
};

const initialState: LearningSlice = {
    error: {
        Mandatory: null,
        Recommended: null,
        Library: null,
        CareerRoadmap: null,
    },
    pointsError: null,

    learningCards: {
        Mandatory: INITIAL_DATA,
        Recommended: INITIAL_DATA,
        Library: INITIAL_DATA,
        CareerRoadmap: INITIAL_DATA,
    },
    learningCardsRequestData: {
        Mandatory: INITIAL_REQUEST,
        Recommended: INITIAL_REQUEST,
        Library: INITIAL_REQUEST,
        CareerRoadmap: INITIAL_REQUEST,
    },
    bookmarks: [],
    userScoreResponse: {
        score: 0,
        unseenScores: [],
    },

    isGetMandatoryLearningCardsPending: false,
    isGetRecommendedLearningCardsPending: false,
    isGetLibraryLearningCardsPending: false,
    isGetCareerRoadmapLearningCardsPending: false,
    isGetBookmarksPending: false,
    isToggleBookmarkPending: false,
    isGetPointsPending: false,
    isMarkPointAsSeenPending: false,
};

export const getMandatoryLearningCards = createAsyncThunk<
    PaginatedResponse<LearningResponse, LearningResultsDescription>,
    CommonSearchRequest,
    { rejectValue: any }
>('learning/Mandatory', (payload, { rejectWithValue }) =>
    getApiLearning(LEARNING_PATH_TYPES.Mandatory, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getRecommendedLearningCards = createAsyncThunk<
    PaginatedResponse<LearningResponse, LearningResultsDescription>,
    CommonSearchRequest,
    { rejectValue: any }
>('learning/Recommended', (payload, { rejectWithValue }) =>
    getApiLearning(LEARNING_PATH_TYPES.Recommended, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getLibraryLearningCards = createAsyncThunk<
    PaginatedResponse<LearningResponse, LearningResultsDescription>,
    CommonSearchRequest,
    { rejectValue: any }
>('learning/Library', (payload, { rejectWithValue }) =>
    getApiLearning(LEARNING_PATH_TYPES.Library, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getCareerRoadmapLearningCards = createAsyncThunk<
    PaginatedResponse<LearningResponse, LearningResultsDescription>,
    CommonSearchRequest,
    { rejectValue: any }
>('learning/CareerRoadmap', (payload, { rejectWithValue }) =>
    getApiLearning(LEARNING_PATH_TYPES.CareerRoadmap, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const toggleBookmark = createAsyncThunk<any, number, { rejectValue: any }>(
    'learning/toggleBookmark',
    (id, { rejectWithValue }) =>
        putApiToggleLearningBookmark(id)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const getBookmarks = createAsyncThunk<number[], void, { rejectValue: any }>(
    'learning/getBookmarks',
    (_, { rejectWithValue }) =>
        getApiLearningBookmarks()
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const getPoints = createAsyncThunk<UserScoreResponse, void, { rejectValue: any }>(
    'learning/getPoints',
    (_, { rejectWithValue }) =>
        getApiPoints()
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const markPointAsSeen = createAsyncThunk<void, string, { rejectValue: any }>(
    'learning/markPointAsSeen',
    (date, { rejectWithValue }) =>
        deleteApiMarkPointAsSeen(date)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const updateLearning = (
    learningCards: Draft<PaginatedResponse<LearningResponse, LearningResultsDescription>>,
    newData: PaginatedResponse<LearningResponse, LearningResultsDescription>,
) => {
    if (learningCards.pagination.pageNumber < newData.pagination.pageNumber) {
        learningCards.data = [...learningCards.data, ...newData.data];
    } else {
        learningCards.data = newData.data;
    }
    learningCards.pagination = newData.pagination;
    learningCards.resultsDescription = newData.resultsDescription;
    return learningCards;
};

const learningSlice = createSlice({
    name: 'learning',
    initialState,
    reducers: {
        updateLearningCardsRequestData: (
            state,
            action: PayloadAction<{ learningPathType: LearningPathType; requestData: CommonSearchRequest }>,
        ) => {
            const { learningPathType, requestData } = action.payload;
            state.learningCardsRequestData[learningPathType] = requestData;
        },
        updateLearningCardsSearchForAll: (
            state,
            action: PayloadAction<{
                textSearch: string;
                pageNumber: number;
                pageSize: number;
            }>,
        ) => {
            const { textSearch, pageNumber, pageSize } = action.payload;
            for (const learningPathType of Object.values(LEARNING_PATH_TYPES)) {
                state.learningCardsRequestData[learningPathType] = {
                    ...state.learningCardsRequestData[learningPathType],
                    textSearch,
                    pageNumber,
                    pageSize,
                };
            }
        },
        setLearningBookmarkState: (
            state,
            action: PayloadAction<{
                id: number;
                isBookmarked: boolean;
            }>,
        ) => {
            const { id, isBookmarked } = action.payload;

            if (isBookmarked) {
                if (!state.bookmarks.includes(id)) {
                    state.bookmarks.push(id);
                }
            } else {
                state.bookmarks = state.bookmarks.filter((bookmarkId) => bookmarkId !== id);
            }

            for (const learningPathType of Object.values(LEARNING_PATH_TYPES)) {
                state.learningCards[learningPathType].data.forEach((learning) => {
                    if (learning.id !== id || !learning.userActivity) return;

                    if (isBookmarked) {
                        if (!learning.userActivity.states.includes(LEARNING_STATES.Bookmarked)) {
                            learning.userActivity.states.push(LEARNING_STATES.Bookmarked);
                        }
                        return;
                    }

                    learning.userActivity.states = learning.userActivity.states.filter(
                        (state) => state !== LEARNING_STATES.Bookmarked,
                    );
                });
            }
        },
        deleteLearningCardFromList: (
            state,
            action: PayloadAction<{
                learningPathType: LearningPathType;
                id: number;
            }>,
        ) => {
            const { learningPathType, id } = action.payload;
            const learningCards = state.learningCards[learningPathType];

            const cardExists = learningCards.data.some((learning) => learning.id === id);
            learningCards.data = learningCards.data.filter((learning) => learning.id !== id);

            if (cardExists && learningCards.pagination.totalCount > 0) {
                learningCards.pagination.totalCount -= 1;
            }
        },
        clearLearnings: (state, action: PayloadAction<{ learningPathType: LearningPathType }>) => {
            const { learningPathType } = action.payload;

            state.learningCards[learningPathType] = {
                ...INITIAL_DATA,
                data: [],
                pagination: { ...INITIAL_DATA.pagination },
                resultsDescription: { ...INITIAL_DATA.resultsDescription },
            };

            state.learningCardsRequestData[learningPathType] = {
                ...INITIAL_REQUEST,
                filters: null,
            };

            state.error[learningPathType] = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // MANDATORY
            .addCase(getMandatoryLearningCards.pending, (state) => {
                state.error.Mandatory = null;
                state.isGetMandatoryLearningCardsPending = true;
            })
            .addCase(getMandatoryLearningCards.fulfilled, (state, action) => {
                const newData = action.payload;
                const learningCards = state.learningCards.Mandatory;

                updateLearning(learningCards, newData);
                state.isGetMandatoryLearningCardsPending = false;
            })
            .addCase(getMandatoryLearningCards.rejected, (state, action) => {
                state.error.Mandatory = action.payload ?? action.error;
                state.isGetMandatoryLearningCardsPending = false;
            })

            // RECOMMENDED
            .addCase(getRecommendedLearningCards.pending, (state) => {
                state.error.Recommended = null;
                state.isGetRecommendedLearningCardsPending = true;
            })
            .addCase(getRecommendedLearningCards.fulfilled, (state, action) => {
                const newData = action.payload;
                const learningCards = state.learningCards.Recommended;

                updateLearning(learningCards, newData);
                state.isGetRecommendedLearningCardsPending = false;
            })
            .addCase(getRecommendedLearningCards.rejected, (state, action) => {
                state.error.Recommended = action.payload ?? action.error;
                state.isGetRecommendedLearningCardsPending = false;
            })

            // LIBRARY
            .addCase(getLibraryLearningCards.pending, (state) => {
                state.error.Library = null;
                state.isGetLibraryLearningCardsPending = true;
            })
            .addCase(getLibraryLearningCards.fulfilled, (state, action) => {
                const newData = action.payload;
                const learningCards = state.learningCards.Library;

                updateLearning(learningCards, newData);
                state.isGetLibraryLearningCardsPending = false;
            })
            .addCase(getLibraryLearningCards.rejected, (state, action) => {
                state.error.Library = action.payload ?? action.error;
                state.isGetLibraryLearningCardsPending = false;
            })

            // CAREER ROADMAP
            .addCase(getCareerRoadmapLearningCards.pending, (state) => {
                state.error.CareerRoadmap = null;
                state.isGetCareerRoadmapLearningCardsPending = true;
            })
            .addCase(getCareerRoadmapLearningCards.fulfilled, (state, action) => {
                const newData = action.payload;
                const learningCards = state.learningCards.CareerRoadmap;

                updateLearning(learningCards, newData);
                state.isGetCareerRoadmapLearningCardsPending = false;
            })
            .addCase(getCareerRoadmapLearningCards.rejected, (state, action) => {
                state.error.CareerRoadmap = action.payload ?? action.error;
                state.isGetCareerRoadmapLearningCardsPending = false;
            })

            // BOOKMARKS
            .addCase(getBookmarks.pending, (state) => {
                state.isGetBookmarksPending = true;
            })
            .addCase(getBookmarks.fulfilled, (state, action) => {
                state.bookmarks = action.payload;
                state.isGetBookmarksPending = false;
            })
            .addCase(getBookmarks.rejected, (state) => {
                state.isGetBookmarksPending = false;
            })

            // TOGGLE BOOKMARK
            .addCase(toggleBookmark.pending, (state) => {
                state.isToggleBookmarkPending = true;
            })
            .addCase(toggleBookmark.fulfilled, (state) => {
                state.isToggleBookmarkPending = false;
            })
            .addCase(toggleBookmark.rejected, (state) => {
                state.isToggleBookmarkPending = false;
            })

            // POINTS
            .addCase(getPoints.pending, (state) => {
                state.pointsError = null;
                state.isGetPointsPending = true;
            })
            .addCase(getPoints.fulfilled, (state, action) => {
                state.userScoreResponse = action.payload;
                state.isGetPointsPending = false;
            })
            .addCase(getPoints.rejected, (state, action) => {
                state.pointsError = action.payload ?? action.error.message ?? null;
                state.isGetPointsPending = false;
            })

            // MARK POINT AS SEEN
            .addCase(markPointAsSeen.pending, (state) => {
                state.pointsError = null;
                state.isMarkPointAsSeenPending = true;
            })
            .addCase(markPointAsSeen.fulfilled, (state, action) => {
                state.userScoreResponse.unseenScores = state.userScoreResponse.unseenScores.filter(
                    (point) => point.eventDateTimeUTC !== action.meta.arg,
                );
                state.isMarkPointAsSeenPending = false;
            })
            .addCase(markPointAsSeen.rejected, (state, action) => {
                state.pointsError = action.payload ?? action.error.message ?? null;
                state.isMarkPointAsSeenPending = false;
            });
    },
});

export const {
    updateLearningCardsRequestData,
    updateLearningCardsSearchForAll,
    setLearningBookmarkState,
    clearLearnings,
    deleteLearningCardFromList,
} = learningSlice.actions;

export default learningSlice.reducer;
